import * as turf from "@turf/turf";
import type { FormikProps } from "formik";
import moment from "moment/min/moment-with-locales";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "senf-shared/auth/AuthProvider";
import { useGlobalState } from "senf-shared/globalState/GlobalState";
import { generateLink, getPhaseOfDateRange, openLink } from "senf-shared/index";
import type { Project } from "senf-shared/types";
import { useConvertCase } from "senf-shared/utils/convertCase";
import { getZoomLevel } from "../../atoms/map/utils/helpers";
import type { IconName } from "../../core/icon/names";
import { pageTypes } from "./pageTypes";
import type {
  Question,
  QuestionColumn,
  QuestionOptionsKey,
  QuestionValue,
  Result,
  SurveyImage,
} from "./survey.types";
import { getMostRecentlyViewedPageIndex } from "./utils";

// for compatibility with old surveys, imgUrls can be  a string or array
const isImageValid = (img: string | SurveyImage[] | undefined): boolean => {
  if (img === undefined) return false;
  if (typeof img === "string" || Array.isArray(img)) return !!img.length;
  return false;
};

type SurveyNavigationProps = {
  pages: Question[];
  index: number;
  formik?: FormikProps<Result["values"]>;
};

type PrepareSurveyPageProps = SurveyNavigationProps & {
  isMobile?: boolean;
  project?: Project;
};

const getCondition = ({ pages, index, formik }: SurveyNavigationProps) => {
  const page = pages[index];
  const { value } = (page && formik?.values[page.uid]) || {};
  return (
    page?.conditions?.find(({ type, response }) => {
      if (type === "equals") {
        return response === value || value?.includes(response);
      }
      if (type === "notEquals") {
        return response !== value && !value?.includes(response);
      }
      return false;
    }) || page?.conditions?.[0]
  );
};

const getNextIndex = ({ pages, index, formik }: SurveyNavigationProps) => {
  const condition = getCondition({ pages, index, formik });
  const incrementByOne =
    pages[index]?.qtype === "intro" ||
    !condition ||
    condition.goToPage === "next";
  return incrementByOne
    ? index + 1
    : pages.findIndex((e) => e.uid === condition.goToPage);
};

const useProfileCompletedInOtherSession = () => {
  const { user } = useAuthContext();
  const key = useMemo(() => `${user?.userId}-profileCompleted`, [user?.userId]);
  const isProfileCompleted = () =>
    user && sessionStorage.getItem(key) === "true";
  useEffect(() => {
    if (user && sessionStorage.getItem(key) == null)
      sessionStorage.setItem(key, user.profileCompleted.toString());
  }, [user, key]);
  return isProfileCompleted;
};

export const useNavigationIndex = ({
  pages,
  index,
  formik,
}: SurveyNavigationProps) => {
  const isProfileCompleted = useProfileCompletedInOtherSession();

  const [nextIndex, setNextIndex] = useState(index + 1);
  const [previousIndex, setPreviousIndex] = useState(index - 1);

  useEffect(() => {
    const profileCompleted = isProfileCompleted();

    let prev = getMostRecentlyViewedPageIndex(pages, formik?.values, index, 1);
    // this is a special case when demographics is the first page and it is completed, then there is no timestamp for this page
    if (pages[prev]?.qtype === "demographics" && profileCompleted) {
      prev--;
    }
    setPreviousIndex(prev);

    let next = getNextIndex({ pages, index, formik });
    if (pages[next]?.qtype === "demographics" && profileCompleted) {
      next = getNextIndex({ pages, index: next, formik });
    }
    setNextIndex(next);
  }, [index, formik?.values]);
  return {
    nextIndex,
    previousIndex,
    isLastPage: pages[nextIndex]?.qtype === "outro",
  };
};

export type ChartViews =
  | "grid"
  | "heatmap"
  | "drawn"
  | "hexabin"
  | "table"
  | "pie"
  | "bar"
  | "column"
  | "wordcloud"
  | "list";

type ChartViewBaseProps = {
  icon: IconName;
  type: "map" | "chart" | "wordcloud" | "table";
};
export type ChartViewConfig = Record<
  ChartViews,
  ChartViewBaseProps & {
    text: string;
    active: boolean;
  }
>;

export const gridTypeProps: Record<ChartViews, ChartViewBaseProps> = {
  heatmap: {
    icon: "map-heat",
    type: "map",
  },
  hexabin: {
    icon: "map-hexabin",
    type: "map",
  },
  drawn: {
    icon: "map-drawn",
    type: "map",
  },
  grid: {
    icon: "statistics-grid",
    type: "chart",
  },
  pie: {
    icon: "statistics-pie",
    type: "chart",
  },
  bar: {
    icon: "statistics-bar",
    type: "chart",
  },
  column: {
    icon: "statistics-column",
    type: "chart",
  },
  wordcloud: {
    icon: "wordcloud",
    type: "wordcloud",
  },
  table: {
    icon: "table",
    type: "table",
  },
  list: {
    icon: "list",
    type: "table",
  },
};

export const usePrepareSurveyPage = ({
  pages,
  index,
  isMobile,
  project,
}: PrepareSurveyPageProps) => {
  const { t } = useTranslation();
  const { toTitleCase } = useConvertCase();
  const getPageByIndex = (i = index) => {
    const currentPage = pages?.[i];
    const currentPageMapData =
      (currentPage?.mapId &&
        currentPage?.mapId !== "" &&
        project?.maps?.find((map) => map.mapId === currentPage?.mapId)) ||
      null;

    // const { mapDataLoading, error, mapData } = useMapData(
    //   project,
    //   currentPage?.mapId
    // );

    const outroIndex = (pages?.length || 0) - 1;

    const pageRoles = ["intro", "outro", "demographics"];

    const currentPageRole = pageRoles.includes(currentPage?.qtype)
      ? currentPage?.qtype
      : "surveyPage";

    const currentPageIsMapbased =
      currentPage?.qtype === "point" ||
      currentPage?.qtype === "path" ||
      currentPage?.qtype === "area";

    const currentPageHasMap =
      currentPageIsMapbased ||
      (currentPage?.mapId && currentPage?.mapId !== "");

    if ((currentPageIsMapbased || currentPageHasMap) && currentPageMapData) {
      const [longitude, latitude] = turf.center(currentPageMapData?.mapbounds)
        ?.geometry?.coordinates || [51.1657, 10.4515];
      const zoom = getZoomLevel(turf.bbox(currentPageMapData?.mapbounds));

      currentPageMapData.initialMapViewport = {
        latitude,
        longitude,
        zoom,
        duration: 0,
        pitch: 0,
        bearing: 50,
        maxZoom: 20,
        minZoom: 5,
      };
    }

    const currentPageHasImage = isImageValid(currentPage?.imgUrl);

    const currentPageHasQuestion = !pageTypes().find(
      (qt) => qt.value === currentPage?.qtype,
    )?.structuralPage;

    const currentPageImages =
      typeof currentPage?.imgUrl === "string"
        ? [{ url: currentPage?.imgUrl }]
        : currentPage?.imgUrl;

    const currentPageAlignment =
      (currentPage?.qtype === "intro" || currentPage?.qtype === "outro") &&
      ((!currentPageHasImage && !currentPageHasMap) || isMobile)
        ? ("center" as const)
        : undefined;

    const progress = Math.max(0, Math.min(i / outroIndex, 1) * 100);

    return {
      currentPage,
      currentPageRole,
      currentPageIsMapbased,
      currentPageHasMap,
      currentPageHasImage,
      currentPageHasQuestion,
      currentPageImages,
      currentPageAlignment,
      currentPageMapData,
      progress,
    };
  };

  const getViewsConfig = (currentPageIndex: number) => {
    const { currentPageIsMapbased, currentPage, currentPageRole } =
      getPageByIndex(currentPageIndex);
    const isSurveyPage = currentPageRole === "surveyPage";
    return {
      heatmap: {
        ...gridTypeProps.heatmap,
        text: toTitleCase(t("global:heatmap")),
        active: currentPageIsMapbased,
      },
      drawn: {
        ...gridTypeProps.drawn,
        text: toTitleCase(t("global:map")),
        active: currentPageIsMapbased,
      },
      hexabin: {
        ...gridTypeProps.hexabin,
        text: toTitleCase(t("global:hexabin")),
        active: currentPageIsMapbased,
      },
      grid: {
        ...gridTypeProps.grid,
        text: toTitleCase(t("global:gridChart")),
        active:
          isSurveyPage &&
          !currentPageIsMapbased &&
          currentPage?.qtype !== "text",
      },
      pie: {
        ...gridTypeProps.pie,
        text: toTitleCase(t("global:pieChart")),
        active:
          isSurveyPage &&
          !!(
            !currentPageIsMapbased ||
            currentPage?.mapbasedQtype === "textarea" ||
            currentPage?.mapbasedOtherOption
          ),
      },
      bar: {
        ...gridTypeProps.bar,
        text: toTitleCase(t("global:barChart")),
        active:
          isSurveyPage &&
          !!(
            !currentPageIsMapbased ||
            currentPage?.mapbasedQtype === "textarea" ||
            currentPage?.mapbasedOtherOption
          ),
      },
      column: {
        ...gridTypeProps.column,
        text: toTitleCase(t("global:columnChart")),
        active:
          isSurveyPage &&
          !!(
            !currentPageIsMapbased ||
            currentPage?.mapbasedQtype === "textarea" ||
            currentPage?.mapbasedOtherOption
          ),
      },
      wordcloud: {
        ...gridTypeProps.wordcloud,
        text: toTitleCase(t("global:wordCloud")),
        active:
          currentPage?.qtype === "text" ||
          currentPage?.mapbasedQtype === "textarea",
      },
      table: {
        ...gridTypeProps.table,
        text: toTitleCase(t("global:table")),
        active:
          (currentPageIsMapbased &&
            (currentPage?.mapbasedQtype === "textarea" ||
              currentPage?.mapbasedOtherOption)) ||
          currentPage?.qtype === "text" ||
          currentPage?.qtype === "number",
      },
    } as ChartViewConfig;
  };

  return {
    ...getPageByIndex(),
    getPageByIndex,
    getViewsConfig,
  };
};

export const useOverflow = (ref?: React.RefObject<HTMLDivElement>) => {
  const [scrollLeft, setScrollLeft] = useState<number>(0);
  const { scrollWidth, clientWidth } = ref?.current || {
    scrollWidth: 0,
    clientWidth: 0,
  };
  useEffect(() => {
    const r = ref?.current;
    const handleScroll = () => setScrollLeft(r?.scrollLeft || 0);
    r?.addEventListener("scroll", handleScroll);
    return () => r?.removeEventListener("scroll", handleScroll);
  }, []);

  const direction = document.documentElement.getAttribute("dir");
  const start =
    scrollWidth > clientWidth &&
    (direction === "ltr" ? scrollLeft > 5 : scrollLeft < scrollWidth - 5);
  const end =
    scrollWidth > clientWidth &&
    (direction === "ltr"
      ? scrollLeft + clientWidth < scrollWidth - 5
      : scrollLeft < 5);

  return {
    start,
    end,
  };
};

const storedRandomOrder: Record<string, QuestionColumn[]> = {};

const generate = (arr: QuestionColumn[]) =>
  Array.from(arr, () => Math.floor(Math.random() * 100));

const isAscending = (arr: number[]) =>
  arr.every(
    (value, index, array) =>
      index === array.length - 1 || value < array[index + 1],
  );

const randomize = (arr: QuestionColumn[]): QuestionColumn[] => {
  let order = [] as number[];
  while (isAscending(order)) order = generate(arr);

  return arr
    .map((e, i) => ({ item: e, order: order[i] }))
    .sort((a, b) => a.order - b.order)
    .map(({ item }) => item);
};

export const useRandomSorting = (
  page: Question,
  key: QuestionOptionsKey,
  mode: "edit" | "display" = "display",
  remember = true,
) => {
  const [sortedOptions, setSortedOptions] = useState<QuestionColumn[]>(
    page[key] || [],
  );

  useEffect(() => {
    if (mode === "display" && page?.randomSorting && page[key]) {
      const currentOptions = page[key] || [];
      const storedOrder = storedRandomOrder[page.uid];
      const optionsChanged =
        !storedOrder || storedOrder.length !== currentOptions.length;

      if (!storedOrder || !remember || optionsChanged) {
        // Generate new random order if:
        // - Not in memory
        // - Not remembering
        // - Options have changed
        const newOrder = randomize(currentOptions);
        storedRandomOrder[page.uid] = newOrder;
        setSortedOptions(newOrder);
      } else {
        setSortedOptions(storedOrder);
      }
    } else {
      setSortedOptions(page[key] || []);
    }
  }, [mode, page, key, remember]);

  return sortedOptions;
};

export const useSurveyImageSettings = () => {
  return {
    rowGrid: 5,
    defaultImageDimensions: {
      w: 2,
      h: 5,
      x: 0,
      y: 0,
    },
  };
};

interface UseVerifyFilesProps {
  mimeTypes?: string[];
  maxSize?: number;
}
export const useVerifyFiles = ({
  mimeTypes = ["image/", "video/"], // Added "video/" to support video files
  maxSize = 20000000, // 20 MB
}: UseVerifyFilesProps = {}) => {
  const [invalidType, setInvalidType] = useState(false);
  const [invalidSize, setInvalidSize] = useState(false);

  const validType = (type: string) =>
    mimeTypes.some((mimeType) => type.startsWith(mimeType));

  const validSize = (size: number) => size < maxSize;

  const checkFileUpload = ({ files }) => {
    const validFiles = [] as File[];

    [...(files || [])].forEach((file) => {
      if (validType(file.type)) {
        if (validSize(file.size)) validFiles.push(file);
        else setInvalidSize(true);
      } else setInvalidType(true);
    });

    setTimeout(
      () => {
        setInvalidSize(false);
        setInvalidType(false);
      },
      validFiles.length ? 0 : 2000,
    );

    return validFiles;
  };

  return { invalidType, invalidSize, checkFileUpload };
};

export const useFormatDuration = () => {
  const { i18n } = useTranslation();

  const formatDuration = (duration: number) => {
    if (duration < 1000) return "< 1s";
    return moment
      .utc(moment.duration(duration).asMilliseconds())
      .format(duration >= 3600000 ? "H[h] m[m] s[s]" : "m[m] s[s]", {
        locale: i18n.resolvedLanguage,
      });
  };
  return { formatDuration };
};

const getValuesForKey = <T extends object = Result["values"]>(
  array: { values: T }[],
  key: keyof T,
) => {
  return array
    .map((obj) => {
      const newObj = {} as T;
      if (obj.values?.[key]) {
        newObj[key] = obj.values[key];
      }
      return newObj;
    })
    .filter((obj) => key in obj && (obj[key]?.value || obj[key]?.geoData));
};

export const useGenerateStats = ({
  results,
  pages,
}: {
  results: Result[];
  pages: Question[];
}) => {
  const { i18n } = useTranslation();

  const pagesById = pages.reduce(
    (acc, page) => {
      acc[page.uid] = page;
      return acc;
    },
    {} as Record<string, Question>,
  );

  const completeDemographics = (result: Result) => {
    const { age, gender, userPostcode } = result.userData || result;
    return age || gender || userPostcode;
  };

  /**
   * Returns the results that were answered for a specific question
   * @returns
   */
  const answeredQuestion = ({ qtype, uid }: Question, r = results || []) =>
    qtype === "section"
      ? r
      : qtype === "demographics"
        ? r.filter(completeDemographics)
        : getValuesForKey(r, uid);

  // const hasValue = (values: Result, question: Question) => {
  //   if (question.qtype === "demographics") return completeDemographics(values);

  //   return getValuesForKey(results, question.uid).length
  //   const r = values.values[question.uid];
  //   console.log(question?.qtext, r);
  //   return r?.value || r?.geoData || r?.comment;
  // };
  // const legacyBounceRate = true;
  const legacyBounceRate = !results?.some(({ values }) =>
    Object.values(values).some((e) => e.createdAt),
  );

  let paddedResults = (results || []).map((result) => {
    const intro = {
      [pages[0]?.uid]: { timeStamp: result.createdAt } as QuestionValue,
    };

    const firstPage = {
      [pages[1]?.uid]: { createdAt: result.createdAt } as QuestionValue,
    };

    const outro = {
      [pages[pages.length - 1]?.uid]: {
        timeStamp: result.createdAt,
      } as QuestionValue,
    };

    const paddedValues = {
      ...intro,
      // in case no entries were submitted, we need to set the first page as viewed
      ...firstPage,
      ...result.values,
      // in case the survey was completed, we need to set the outro
      // so that the last page is not counted as bounced
      ...(result.completed ? outro : {}),
    };

    return { ...result, values: paddedValues };
  });

  if (legacyBounceRate) {
    paddedResults = paddedResults.map((result) => {
      const entries = Object.entries(result.values);
      const [lastEntryId, lastEntryValue] =
        entries.filter(([, value]) => !!value.timeStamp).pop() || [];
      const currentPageIndex = pages.findIndex((e) => e.uid === lastEntryId);
      const nextPage = pages[currentPageIndex + 1];

      if (!nextPage) return result;

      const answeredQuestions = Object.fromEntries(
        entries.map(([uid, value]) => [
          uid,
          {
            ...value,
            ...(nextPage && value.timeStamp
              ? { createdAt: value.timeStamp }
              : {}),
          },
        ]),
      );

      return {
        ...result,
        values: {
          ...answeredQuestions,
          [nextPage.uid]: {
            value: `legacy bounced page ${nextPage.qtext || nextPage.qtype}`,
            createdAt: new Date(
              lastEntryValue?.timeStamp ?? new Date().getTime() + 100,
            ).toISOString(),
          },
        },
      };
    });
  }

  const totalsRaw = {} as Record<string, number[]>;

  const raw =
    paddedResults.reduce(
      (acc, result) => {
        const entries = Object.entries(result.values);
        entries
          .sort(
            ([, a], [, b]) =>
              new Date(a?.createdAt || a?.viewed || "").getTime() -
              new Date(b?.createdAt || b?.viewed || "").getTime(),
          )
          .forEach(([uid, value], i, arr) => {
            if (
              pagesById[uid]?.qtype === "intro" ||
              pagesById[uid]?.qtype === "outro"
            )
              return;
            if (!acc[uid])
              acc[uid] = {
                bounced: 0,
                ms: [],
              };

            if (i === arr.length - 1) {
              acc[uid].bounced = (acc[uid].bounced || 0) + 1;
            }
            if (!acc[uid].ms) acc[uid].ms = [];
            const [, nextValue] = arr[i + 1] || [];
            const ms = Math.max(
              0,
              new Date(
                nextValue?.createdAt ||
                  nextValue?.viewed ||
                  result.completed ||
                  nextValue?.timeStamp ||
                  0,
              ).getTime() -
                new Date(value.createdAt || value.viewed || 0).getTime(),
            );

            if (ms < 300000) {
              acc[uid].ms.push(ms);
              if (!totalsRaw[result.entryId]) totalsRaw[result.entryId] = [];
              totalsRaw[result.entryId].push(ms);
            }
          });
        return acc;
      },
      {} as Record<string, { bounced: number; ms: number[] }>,
    ) || {};

  const totals = Object.values(totalsRaw)
    /**
     * do not filter those out because we also want to see the average completion time if no one completed the survey
     */
    //.filter((e) => e.length > (pages.length - 3))
    .map((e) => e.reduce((acc, curr) => acc + curr, 0))
    .sort((a, b) => a - b);

  const stats = Object.fromEntries(
    Object.entries(raw).map(([pageUid, value]) => {
      const answered = answeredQuestion(pagesById[pageUid] || {});
      const ms = value.ms.sort((a, b) => a - b);
      const total = ms.reduce((acc, curr) => acc + curr, 0);
      return [
        pageUid,
        {
          ...value,
          ms,
          total,
          bounceRate: value.bounced / results.length,
          median: ms[Math.floor(ms.length / 2)],
          average: total / (answered.length || 1),
        },
      ];
    }),
  );

  const getPageStats = (index: number) => {
    const page = pages[index];
    const { bounced, median, average } = stats[page.uid] || {};
    const answeredQuestionsRate = `${
      answeredQuestion(page || {}).length || 0
    } / ${results?.length || 1}`;

    const bounceRate = new Intl.NumberFormat(i18n.resolvedLanguage, {
      style: "percent",
      maximumFractionDigits: 1,
    }).format((bounced || 0) / (results?.length || 1));
    return { bounced, median, average, bounceRate, answeredQuestionsRate };
  };
  return { stats, getPageStats, totals };
};

type UseOutroButtonsParams = {
  preview: boolean;
  project: Project;
  currentPage?: Question;
};

export function useOutroButtons({
  preview,
  project,
  currentPage,
}: UseOutroButtonsParams) {
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;
  const navigate = useNavigate();
  return useMemo(() => {
    const buttons: Array<
      | {
          buttonLabel: string;
          onClick: () => void;
          trailingIcon?: string;
        }
      | {
          type: "addPost";
          ideaMapTitle: string;
          buttonLabel: string;
          onClick: () => void;
          trailingIcon?: string;
        }
    > = [];

    // Info page button
    if (
      (project?.brief || project?.title) &&
      currentPage?.qbuttonInfoPage !== false
    ) {
      buttons.push({
        buttonLabel: t("global:toInfoPage"),
        trailingIcon: !preview ? " " : undefined,
        onClick: () =>
          preview
            ? navigate(`/projects/${project.projectId}/info`)
            : openLink(
                generateLink("mainApp", `projects/${project.projectId}/info`),
              ),
      });
    }

    // Idea map buttons
    for (const ideaMap of project?.ideaMaps || []) {
      const phase = getPhaseOfDateRange(ideaMap.startDate, ideaMap.endDate);
      const isActive = preview
        ? phase === "while"
        : phase === "while" || phase === "before";

      if (
        ideaMap.settings?.publicAccess === false ||
        !isActive ||
        currentPage?.qbuttonIdeaMaps === false
      ) {
        continue;
      }

      buttons.push({
        type: "addPost",
        ideaMapTitle: translation?.[ideaMap.title ?? ""] || ideaMap.title || "",
        buttonLabel: t("survey:successPage.addPost"),
        trailingIcon: !preview ? " " : undefined,
        onClick: () =>
          preview
            ? navigate(
                `/projects/${project.projectId}/ideaMaps/${ideaMap.ideaMapId}/addIdea`,
              )
            : openLink(
                generateLink(
                  "mainApp",
                  `projects/${project.projectId}/ideaMaps/${ideaMap.ideaMapId}/addIdea`,
                ),
              ),
      });
    }

    return buttons;
  }, [preview, project, currentPage, translation, t, navigate]);
}
